import { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import queryString from 'qs';
import useMountEffect from '../../hooks/useMountEffect';
import {
	COMPARISON_MODE,
	ComparisonState,
	DEFAULT_COMPARISON_STATE,
	resolveComparisonRange,
} from '../../utils/AnalyticsComparisonUtils';
import { parseSafeInt } from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import {
	getDefaultSupplierPerformanceFilters,
	queryParamsToSupplierPerformanceComparison,
	supplierPerformanceFiltersToApiParams,
	supplierPerformanceStateToQueryParams,
	SupplierPerformanceFilters,
} from './SupplierPerformanceUtils';
import { BACK_LINK_TEXT, DEFAULT_MIN_WO_COUNT } from './SupplierPerformanceConstants';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';

const filtersWithDateRange = (
	filters: SupplierPerformanceFilters,
	range: [moment.Moment, moment.Moment]
): SupplierPerformanceFilters => ({
	...filters,
	startDate: range[0].clone().startOf('day'),
	endDate: range[1].clone().endOf('day'),
});

export function useSupplierPerformanceData(props: any) {
	const { userType, currentUser, companyConfig, fetchSupplierPerformance } = props;

	const history = useHistory();
	const location = useLocation();

	const {
		initialFilters,
		initialMinWoCount,
		initialShowSupplierLabels,
		initialSuppliersPanelOpen,
		initialComparisonState,
	} = useMemo(() => {
		const search = location.search;
		const str = search && search[0] === '?' ? search.slice(1) : search;
		const queryParams = queryString.parse(str || '');
		return {
			initialFilters: getDefaultSupplierPerformanceFilters(),
			initialMinWoCount: queryParams.minWoCount
				? parseSafeInt(queryParams.minWoCount, DEFAULT_MIN_WO_COUNT)
				: DEFAULT_MIN_WO_COUNT,
			initialShowSupplierLabels: queryParams.labels === 'true',
			initialSuppliersPanelOpen: queryParams.panel !== 'false',
			initialComparisonState: queryParamsToSupplierPerformanceComparison(queryParams),
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [supplierPerformance, setSupplierPerformance] = useState([]);
	const [previousSupplierPerformance, setPreviousSupplierPerformance] = useState([]);
	const [comparisonState, setComparisonState] = useState<ComparisonState>(initialComparisonState);
	const [comparisonLoading, setComparisonLoading] = useState(false);

	const [activeFilters, setActiveFilters] = useState<SupplierPerformanceFilters>(initialFilters);
	const [minWoCount, setMinWoCount] = useState(initialMinWoCount);
	const [showSupplierLabels, setShowSupplierLabels] = useState(initialShowSupplierLabels);
	const [suppliersPanelOpen, setSuppliersPanelOpen] = useState(initialSuppliersPanelOpen);

	const urlStateRef = useRef({
		activeFilters: initialFilters,
		minWoCount: initialMinWoCount,
		showSupplierLabels: initialShowSupplierLabels,
		suppliersPanelOpen: initialSuppliersPanelOpen,
		comparisonState: initialComparisonState,
	});

	const syncUrl = useCallback(
		(patch: Partial<typeof urlStateRef.current>) => {
			const next = { ...urlStateRef.current, ...patch };
			urlStateRef.current = next;
			const params = supplierPerformanceStateToQueryParams(
				next.activeFilters,
				next.minWoCount,
				next.showSupplierLabels,
				next.suppliersPanelOpen,
				next.comparisonState
			);
			const search = queryString.stringify(params);
			history.replace(history.location.pathname + (search ? '?' + search : ''));
		},
		[history]
	);

	const loadSupplierPerformance = useCallback(
		(filters: SupplierPerformanceFilters) => {
			setLoading(true);
			setError('');
			fetchSupplierPerformance({}, supplierPerformanceFiltersToApiParams(filters))
				.then((rows) => {
					setSupplierPerformance(rows || []);
				})
				.catch((e) => {
					// The lifecycle thunk rejects with a plain string (or undefined) rather
					// than an Error, so handle both shapes before falling back.
					const message = e && e.message ? e.message : typeof e === 'string' ? e : '';
					setError(message || 'Failed to load supplier performance data.');
				})
				.finally(() => {
					setLoading(false);
				});
		},
		[fetchSupplierPerformance]
	);

	const loadComparison = useCallback(
		(state: ComparisonState, filters: SupplierPerformanceFilters) => {
			if (!state || state.mode === COMPARISON_MODE.NONE) {
				setPreviousSupplierPerformance([]);
				setComparisonLoading(false);
				return;
			}
			if (!filters || !filters.startDate || !filters.endDate) {
				setPreviousSupplierPerformance([]);
				setComparisonLoading(false);
				return;
			}
			const range = resolveComparisonRange(filters.startDate, filters.endDate, state);
			if (!range) {
				setPreviousSupplierPerformance([]);
				setComparisonLoading(false);
				return;
			}
			const comparisonFilters = filtersWithDateRange(filters, range);
			setComparisonLoading(true);
			fetchSupplierPerformance({}, supplierPerformanceFiltersToApiParams(comparisonFilters))
				.then((rows) => {
					setPreviousSupplierPerformance(rows || []);
				})
				.catch(() => {
					setPreviousSupplierPerformance([]);
				})
				.finally(() => {
					setComparisonLoading(false);
				});
		},
		[fetchSupplierPerformance]
	);
	useMountEffect(() => {
		syncUrl({});
		loadSupplierPerformance(initialFilters);
		loadComparison(initialComparisonState, initialFilters);
	});

	const applyFiltersAndFetch = useCallback(
		(
			newFilters: SupplierPerformanceFilters,
			extraUrlPatch: Partial<typeof urlStateRef.current> = {}
		) => {
			setActiveFilters(newFilters);
			const nextHasFullRange = !!(newFilters && newFilters.startDate && newFilters.endDate);
			let nextComparisonState = urlStateRef.current.comparisonState;
			if (
				!nextHasFullRange &&
				nextComparisonState &&
				nextComparisonState.mode !== COMPARISON_MODE.NONE
			) {
				nextComparisonState = { ...DEFAULT_COMPARISON_STATE };
				setComparisonState(nextComparisonState);
				setPreviousSupplierPerformance([]);
				setComparisonLoading(false);
			}

			loadSupplierPerformance(newFilters);
			loadComparison(nextComparisonState, newFilters);
			syncUrl({
				...extraUrlPatch,
				activeFilters: newFilters,
				comparisonState: nextComparisonState,
			});
		},
		[loadSupplierPerformance, loadComparison, syncUrl]
	);

	const hasFullCompletedAtRange = !!(
		activeFilters &&
		activeFilters.startDate &&
		activeFilters.endDate
	);

	const handleComparisonChange = useCallback(
		(nextState: ComparisonState) => {
			const resolved = nextState || { ...DEFAULT_COMPARISON_STATE };
			setComparisonState(resolved);
			loadComparison(resolved, activeFilters);
			syncUrl({ comparisonState: resolved });
		},
		[activeFilters, loadComparison, syncUrl]
	);

	const updateFilters = useCallback(
		(patch: Partial<SupplierPerformanceFilters>) => {
			applyFiltersAndFetch({ ...activeFilters, ...patch });
		},
		[activeFilters, applyFiltersAndFetch]
	);

	const clearAllFilters = useCallback(() => {
		setMinWoCount(DEFAULT_MIN_WO_COUNT);
		applyFiltersAndFetch(getDefaultSupplierPerformanceFilters(), {
			minWoCount: DEFAULT_MIN_WO_COUNT,
		});
	}, [applyFiltersAndFetch]);

	const handleMinWoCountChange = useCallback(
		(value: number) => {
			setMinWoCount(value);
			syncUrl({ minWoCount: value });
		},
		[syncUrl]
	);

	const handleShowSupplierLabelsChange = useCallback(
		(value: boolean) => {
			setShowSupplierLabels(value);
			syncUrl({ showSupplierLabels: value });
		},
		[syncUrl]
	);

	const handleSuppliersPanelOpenChange = useCallback(
		(value: boolean) => {
			setSuppliersPanelOpen(value);
			syncUrl({ suppliersPanelOpen: value });
		},
		[syncUrl]
	);

	const navigateToSupplierDetail = useCallback(
		(supplierFacilityId) => {
			if (!supplierFacilityId) return;
			const filterParams = supplierPerformanceStateToQueryParams(
				activeFilters,
				minWoCount,
				showSupplierLabels,
				suppliersPanelOpen,
				comparisonState
			);
			const detailPath = '/' + userType + '/analytics/kpi/vendors/detail/' + supplierFacilityId;
			const filterQuery = queryString.stringify(filterParams);
			const link =
				getLinkWIthBackLinkParams(location, BACK_LINK_TEXT, detailPath) +
				(filterQuery ? '&' + filterQuery : '');
			history.push(link);
		},
		[
			activeFilters,
			minWoCount,
			showSupplierLabels,
			suppliersPanelOpen,
			comparisonState,
			userType,
			history,
			location,
		]
	);

	const navigateToFindVendors = useCallback(() => {
		history.push('/' + userType + '/dashboards/overview/all');
	}, [history, userType]);

	return {
		userType,
		currentUser,
		companyConfig,
		loading,
		error,
		supplierPerformance,
		previousSupplierPerformance,
		comparisonState,
		comparisonLoading,
		hasFullCompletedAtRange,
		activeFilters,
		minWoCount,
		setMinWoCount: handleMinWoCountChange,
		showSupplierLabels,
		setShowSupplierLabels: handleShowSupplierLabelsChange,
		suppliersPanelOpen,
		setSuppliersPanelOpen: handleSuppliersPanelOpenChange,
		handleComparisonChange,
		updateFilters,
		clearAllFilters,
		navigateToSupplierDetail,
		navigateToFindVendors,
	};
}
